import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createSession,
  fetchGuardianSpecies,
  selectGuardian,
  resumeSession,
  login,
} from '../services/api';
import { track } from '../services/track';
import { useLocalStorage } from '../hooks/useLocalStorage';
import HatchAccountFlow from '../components/HatchAccountFlow';
import { getCopy, PICK_AGE_TITLE } from '../utils/copy';
import { AGE_BAND_REPRESENTATIVE_AGE, ageBandFromAge } from '../utils/ageBand';

const AGE_BAND_OPTIONS = [
  { ageBand: '6-8', label: '6-8 岁', hint: '西游奇遇', detail: '数数、找规律、认识世界' },
  { ageBand: '9-12', label: '9-12 岁', hint: '五行探险', detail: '历史、地理、科学、诗词' },
];

function HomePage() {
  const navigate = useNavigate();
  // pick-age-band | pick-egg | hatch(起名结契) | login(主人名 + 暗号) | resume(旧版恢复码)
  const [phase, setPhase] = useState('pick-age-band');
  const [ageBand, setAgeBand] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState('');
  const [savedState, setQuestState] = useLocalStorage('huaxia_quest_state', null);
  // 这台设备上已经有灵兽了:直接回书院,不再从"你多大啦"开始(换人在书院里点「换个小主人」)
  const hasSave = Boolean(savedState?.sessionId && savedState?.guardian);

  const [pendingQuestState, setPendingQuestState] = useState(null);
  // 老存档凭恢复码找回后补设账号
  const [returning, setReturning] = useState(false);

  const [loginName, setLoginName] = useState('');
  const [loginSecret, setLoginSecret] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [resumeCodeInput, setResumeCodeInput] = useState('');
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState('');

  useEffect(() => {
    track('app_open', { hasSave });
    if (hasSave) navigate('/map', { replace: true });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchGuardianSpecies()
      .then((data) => setOptions(data))
      .catch(() => setError('水帘洞的仙气还没散开,请稍后再试'))
      .finally(() => setLoading(false));
  }, []);

  const handlePickAgeBand = (selectedAgeBand) => {
    track('age_selected', { ageBand: selectedAgeBand });
    setAgeBand(selectedAgeBand);
    setPhase('pick-egg');
  };

  const handlePickEgg = async (species) => {
    if (selecting) return;
    setSelecting(true);
    setError('');
    try {
      const session = await createSession(AGE_BAND_REPRESENTATIVE_AGE[ageBand]);
      const guardian = await selectGuardian(session.sessionId, species);
      track('egg_selected', { species, ageBand, sessionId: session.sessionId });
      setPendingQuestState({ sessionId: session.sessionId, guardian, ageBand });
      setReturning(false);
      setPhase('hatch');
    } catch (err) {
      setError('蛋壳有点滑,再试一次吧!');
    } finally {
      setSelecting(false);
    }
  };

  const handleHatchDone = (account) => {
    track('account_created', { sessionId: pendingQuestState?.sessionId });
    setQuestState({
      ...pendingQuestState,
      guardian: account.guardian,
      ownerName: account.ownerName,
    });
    navigate('/map');
  };

  const enterQuest = (result) => {
    track('login_success', { sessionId: result.sessionId });
    setQuestState({
      sessionId: result.sessionId,
      guardian: result.guardian,
      masteries: result.masteries,
      ageBand: ageBandFromAge(result.age),
      ownerName: result.ownerName,
    });
    navigate('/map');
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (loggingIn) return;
    setLoginError('');
    if (!loginName.trim() || !loginSecret) {
      setLoginError('名字和暗号都要填哦');
      return;
    }
    setLoggingIn(true);
    try {
      enterQuest(await login(loginName.trim(), loginSecret));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : '网络有点慢,再试一次吧');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleResumeSubmit = async (event) => {
    event.preventDefault();
    if (resuming) return;
    setResumeError('');
    if (!resumeCodeInput.trim()) {
      setResumeError('请输入恢复码');
      return;
    }
    setResuming(true);
    try {
      const result = await resumeSession(resumeCodeInput.trim());
      if (result.hasAccount || !result.guardian) {
        enterQuest(result);
        return;
      }
      // 老存档还没有主人名:先补设账号,再进入冒险
      setPendingQuestState({
        sessionId: result.sessionId,
        guardian: result.guardian,
        masteries: result.masteries,
        ageBand: ageBandFromAge(result.age),
      });
      setReturning(true);
      setPhase('hatch');
    } catch (err) {
      setResumeError('没找到这个恢复码,再检查一下有没有输错?');
    } finally {
      setResuming(false);
    }
  };

  if (hasSave && phase !== 'hatch') return null;

  if (phase === 'hatch') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-qiji-paper px-4 py-10">
        <HatchAccountFlow
          sessionId={pendingQuestState.sessionId}
          guardian={pendingQuestState.guardian}
          ageBand={pendingQuestState.ageBand}
          returning={returning}
          hatching={!returning}
          onDone={handleHatchDone}
        />
      </div>
    );
  }

  const loginLink = (
    <button
      type="button"
      onClick={() => setPhase('login')}
      className="mt-8 inline-flex min-h-[44px] items-center px-2 text-sm font-medium text-gray-600 underline"
    >
      已经有灵兽?回到它身边
    </button>
  );

  if (phase === 'pick-age-band') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-qiji-paper px-4 py-10">
        <h1 className="text-4xl font-extrabold text-gray-900 text-center">🏮 华夏奇迹录</h1>
        <p className="mt-1 text-center text-sm font-medium text-gray-600">水帘洞</p>
        <p className="mt-3 text-center text-gray-600 text-lg">{PICK_AGE_TITLE}</p>
        <div className="mt-10 flex flex-wrap justify-center gap-5">
          {AGE_BAND_OPTIONS.map((opt) => (
            <button
              key={opt.ageBand}
              type="button"
              data-testid={`age-band-${opt.ageBand}`}
              onClick={() => handlePickAgeBand(opt.ageBand)}
              className="flex w-40 flex-col items-center gap-2 rounded-2xl border-2 border-black
                         bg-white p-6 shadow-brutal transition
                         hover:-translate-y-1 hover:shadow-brutal-sm
                         active:translate-y-0 active:shadow-none"
            >
              <span className="text-2xl font-extrabold text-gray-900">{opt.label}</span>
              <span className="text-base font-bold text-gray-800">{opt.hint}</span>
              <span className="text-sm text-gray-600">{opt.detail}</span>
            </button>
          ))}
        </div>
        {loginLink}
      </div>
    );
  }

  // 从登录/恢复码页返回:没选过年龄段就回到第一屏
  const backToStart = () => {
    setPhase(ageBand ? 'pick-egg' : 'pick-age-band');
    setLoginError('');
    setResumeError('');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-qiji-paper px-4 py-10">
      <h1 className="text-4xl font-extrabold text-gray-900 text-center">
        🏮 华夏奇迹录
      </h1>
      <p className="mt-3 text-center text-gray-600 text-lg">
        {phase === 'pick-egg'
          ? getCopy(ageBand).eggSubtitle
          : '说出你的名字和暗号,回到灵兽身边'}
      </p>

      {phase === 'pick-egg' && loading && <p className="mt-10 text-gray-400">仙气正在聚集...</p>}

      {phase === 'pick-egg' && error && (
        <p role="alert" className="mt-6 text-red-500 font-medium">
          {error}
        </p>
      )}

      {phase === 'pick-egg' && (
        <>
          <button
            type="button"
            onClick={() => setPhase('pick-age-band')}
            className="mt-3 inline-flex min-h-[44px] items-center rounded-full border-2 border-black bg-white px-4 text-sm font-bold text-gray-800"
          >
            选错年龄了?回去重选
          </button>
          <div className="mt-6 flex flex-wrap justify-center gap-5 max-w-2xl w-full">
            {options.map((opt) => (
              <button
                key={opt.species}
                type="button"
                data-testid={`egg-${opt.species}`}
                onClick={() => handlePickEgg(opt.species)}
                disabled={selecting}
                className="flex w-36 flex-col items-center gap-2 rounded-2xl border-2 border-black
                           bg-white p-6 shadow-brutal transition
                           hover:-translate-y-1 hover:shadow-brutal-sm
                           active:translate-y-0 active:shadow-none
                           disabled:opacity-50 disabled:pointer-events-none"
              >
                <span className="text-5xl">🥚</span>
                <span className="text-2xl">{opt.emoji}</span>
                <span className="text-base font-bold text-gray-800">{opt.label}</span>
              </button>
            ))}
          </div>

          {loginLink}
        </>
      )}

      {phase === 'login' && (
        <form
          onSubmit={handleLoginSubmit}
          className="mt-10 flex w-full max-w-xs flex-col items-center gap-3"
        >
          <input
            aria-label="主人名"
            value={loginName}
            onChange={(e) => setLoginName(e.target.value)}
            placeholder="你的名字"
            maxLength={12}
            className="w-full rounded-lg border-2 border-black px-4 py-3 text-center text-xl font-bold focus:outline-none"
          />
          <input
            aria-label="暗号"
            type="password"
            value={loginSecret}
            onChange={(e) => setLoginSecret(e.target.value)}
            placeholder="暗号"
            maxLength={64}
            className="w-full rounded-lg border-2 border-black px-4 py-3 text-center text-xl font-bold focus:outline-none"
          />
          {loginError && (
            <p role="alert" className="text-sm text-red-500">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={loggingIn}
            className="w-full rounded-lg bg-qiji-mint px-6 py-3 text-base font-bold text-gray-900
                       border-2 border-black shadow-brutal-sm disabled:opacity-50"
          >
            {loggingIn ? '正在呼唤灵兽……' : '回到灵兽身边'}
          </button>
          <button
            type="button"
            onClick={() => {
              setPhase('resume');
              setLoginError('');
            }}
            className="inline-flex min-h-[44px] items-center px-2 text-sm text-gray-600 underline"
          >
            用旧版恢复码找回
          </button>
          <button type="button" onClick={backToStart} className="inline-flex min-h-[44px] items-center px-2 text-sm text-gray-600 underline">
            返回
          </button>
        </form>
      )}

      {phase === 'resume' && (
        <form
          onSubmit={handleResumeSubmit}
          className="mt-10 flex w-full max-w-xs flex-col items-center gap-3"
        >
          <label htmlFor="recovery-code-input" className="text-sm text-gray-600">
            输入恢复码,找回守护兽
          </label>
          <input
            id="recovery-code-input"
            type="text"
            value={resumeCodeInput}
            onChange={(e) => setResumeCodeInput(e.target.value.toUpperCase())}
            placeholder="如 7K2M9X"
            maxLength={6}
            className="w-full rounded-lg border-2 border-black px-4 py-3 text-center text-2xl
                       font-mono font-bold tracking-[0.3em] focus:outline-none"
          />
          {resumeError && (
            <p role="alert" className="text-sm text-red-500">
              {resumeError}
            </p>
          )}
          <button
            type="submit"
            disabled={resuming}
            className="w-full rounded-lg bg-qiji-mint px-6 py-3 text-base font-bold text-gray-900
                       border-2 border-black shadow-brutal-sm disabled:opacity-50"
          >
            {resuming ? '找回中...' : '找回守护兽'}
          </button>
          <button
            type="button"
            onClick={() => {
              setPhase('login');
              setResumeError('');
            }}
            className="inline-flex min-h-[44px] items-center px-2 text-sm text-gray-600 underline"
          >
            返回
          </button>
        </form>
      )}
    </div>
  );
}

export default HomePage;
